import { CharacterProgress, jobLists } from "character";
import { GameData, Job, RelicData } from "enums";
import log from "logger";
import manager from "managers/app";
import { RelicID } from "managers/types/relic-types";
import { InventoryV2, InventoryV3, ProgressV2 } from "types/data";

interface OldItem { id: number; item: GameData.Item; }
interface OldRelic {
	id: number; relic: RelicData.Group; parts: {
		id: number; part: RelicData.Part; steps: {
			id: number; step: RelicData.Step; tasks: {
				id: number;
				task: RelicData.Task;
				jobs: Job[];
			}[]
		}[]
	}[]
}

const oldItems:OldItem[] = getOldItems();
const oldRelics:OldRelic[] = getOldRelics();

function findItemById(id:number):GameData.Item|null{
	const thisItem = oldItems.find((z) => { return z.id === id; });
	if(thisItem){ return thisItem.item; }
	return null;
}

export function mapV2InventoryToV3(v2Inventory:InventoryV2):InventoryV3{
	const inventory:InventoryV3 = {};
	v2Inventory.forEach((entry) => {
		if(Number(entry.value) > 0){
			const thisItem = findItemById(entry.id);
			if(thisItem){
				if(!inventory[thisItem]){
					inventory[thisItem] = Number(entry.value);
				}
			}
		}
	});

	return inventory;
}

export function mapV2CompletedToV3(v2Completed:ProgressV2):CharacterProgress{
	const progress = new CharacterProgress();
	const jobs = Object.keys(v2Completed) as Job[];

	oldRelics.forEach((oldRelic) => {
		const relicType = manager.relics.getRelicType(oldRelic.relic);

		jobs.forEach((job) => {
			const completeList = v2Completed[job];

			if(
				jobLists.relic[oldRelic.relic].includes(job)
				&& completeList.includes(oldRelic.id)
				&& !progress.isComplete(relicType, oldRelic.relic, job)
			){
				progress.setComplete(`${relicType}-${oldRelic.relic}` as RelicID, job, true);
			}
		});

		oldRelic.parts.forEach((oldPart) => {
			jobs.forEach((job) => {
				const completeList = v2Completed[job];

				if(
					jobLists.relic[oldRelic.relic].includes(job)
					&& completeList.includes(oldPart.id)
					&& !progress.isComplete(relicType, oldRelic.relic, oldPart.part, job)
				){
					progress.setComplete(`${relicType}-${oldRelic.relic}-${oldPart.part}` as RelicID, job, true);
				}
			});

			oldPart.steps.forEach((oldStep) => {
				jobs.forEach((job) => {
					const completeList = v2Completed[job];

					if(
						jobLists.relic[oldRelic.relic].includes(job)
						&& completeList.includes(oldStep.id)
						&& !progress.isComplete(relicType, oldRelic.relic, oldPart.part, oldStep.step, job)
					){
						progress.setComplete(`${relicType}-${oldRelic.relic}-${oldPart.part}-${oldStep.step}` as any, job, true); // Using any due to Typscript Limitation
					}
				});

				oldStep.tasks.forEach((oldTask) => {
					jobs.forEach((job) => {
						const completeList = v2Completed[job];

						if(
							oldTask.jobs.includes(job)
							&& completeList.includes(oldTask.id)
							&& !progress.isComplete(relicType, oldRelic.relic, oldPart.part, oldStep.step, oldTask.task, job)
						){
							progress.setComplete(`${relicType}-${oldRelic.relic}-${oldPart.part}-${oldStep.step}-${oldTask.task}` as any, job, true); // Using any due to Typscript Limitation
						}
					});
				});
			});
		});
	});

	log.debug("V2 Completed: ", v2Completed);
	return progress;
}

function getOldItems():OldItem[]{
	return [
		{ id: 144, item: GameData.Item.LONGARMS_COMPOSITE_BOW }, { id: 145, item: GameData.Item.VAMPERS_KNIVES }, { id: 146, item: GameData.Item.SANGUINE_SCEPTER }, { id: 147, item: GameData.Item.ERUDITES_PICATRIX_OF_CASTING },
		{ id: 148, item: GameData.Item.CHAMPIONS_LANCE }, { id: 149, item: GameData.Item.WILDLINGS_CESTI }, { id: 150, item: GameData.Item.AEOLIAN_SCIMITAR }, { id: 151, item: GameData.Item.BARBARIANS_BARDICHE },
		{ id: 152, item: GameData.Item.ERUDITES_PICATRIX_OF_HEALING }, { id: 153, item: GameData.Item.MADMANS_WHISPERING_ROD }, { id: 154, item: GameData.Item.HEAVENS_EYE_MATERIA_III }, { id: 155, item: GameData.Item.SAVAGE_MIGHT_MATERIA_III },
		{ id: 156, item: GameData.Item.SAVAGE_AIM_MATERIA_III }, { id: 157, item: GameData.Item.BATTLEDANCE_MATERIA_III }, { id: 158, item: GameData.Item.QUICKTONGUE_MATERIA_III }, { id: 159, item: GameData.Item.THAVNAIRIAN_MIST },
		{ id: 160, item: GameData.Item.ATMA_OF_THE_LION }, { id: 161, item: GameData.Item.ATMA_OF_THE_WATERBEARER }, { id: 162, item: GameData.Item.ATMA_OF_THE_RAM }, { id: 163, item: GameData.Item.ATMA_OF_THE_CRAB },
		{ id: 164, item: GameData.Item.ATMA_OF_THE_FISH }, { id: 165, item: GameData.Item.ATMA_OF_THE_BULL }, { id: 166, item: GameData.Item.ATMA_OF_THE_SCALES }, { id: 167, item: GameData.Item.ATMA_OF_THE_TWINS },
		{ id: 168, item: GameData.Item.ATMA_OF_THE_SCORPION }, { id: 169, item: GameData.Item.ATMA_OF_THE_ARCHER }, { id: 170, item: GameData.Item.ATMA_OF_THE_GOAT }, { id: 171, item: GameData.Item.ATMA_OF_THE_MAIDEN },
		{ id: 172, item: GameData.Item.QUENCHING_OIL }, { id: 542, item: GameData.Item.LIGHT }, { id: 558, item: GameData.Item.BOMBARD_CORE }, { id: 559, item: GameData.Item.BRONZE_LAKE_CRYSTAL }, { id: 560, item: GameData.Item.SACRED_SPRING_WATER },
		{ id: 561, item: GameData.Item.HORN_OF_THE_BEAST }, { id: 562, item: GameData.Item.GOBMACHINE_BANGPLATE }, { id: 563, item: GameData.Item.NARASIMHA_HIDE }, { id: 564, item: GameData.Item.SICKLE_FANG }, { id: 565, item: GameData.Item.FURNACE_RING_HQ },
		{ id: 566, item: GameData.Item.PERFECT_FIREWOOD_HQ }, { id: 576, item: GameData.Item.ALLAGAN_RESIN }, { id: 577, item: GameData.Item.VALE_BUBO }, { id: 578, item: GameData.Item.VOIDWEAVE }, { id: 579, item: GameData.Item.AMDAPOR_VELLUM },
		{ id: 580, item: GameData.Item.INDIGO_PEARL }, { id: 581, item: GameData.Item.PERFECT_MORTAR_HQ }, { id: 582, item: GameData.Item.PERFECT_PESTLE_HQ }, { id: 589, item: GameData.Item.TAILOR_MADE_EEL_PIE_HQ },
		{ id: 590, item: GameData.Item.PERFECT_CLOTH_HQ }, { id: 591, item: GameData.Item.FURITE_SAND }, { id: 592, item: GameData.Item.BRASS_KETTLE }, { id: 593, item: GameData.Item.PERFECT_VELLUM_HQ }, { id: 594, item: GameData.Item.PERFECT_POUNCE_HQ },
		{ id: 595, item: GameData.Item.TONBERRY_KING_BLOOD }, { id: 596, item: GameData.Item.ROYAL_GIGANT_BLOOD }, { id: 597, item: GameData.Item.KRAKEN_BLOOD }, { id: 598, item: GameData.Item.VICEGERENT_BLOOD },
		{ id: 599, item: GameData.Item.LOST_TREASURE_OF_AMDAPOR }, { id: 600, item: GameData.Item.LOST_TREASURE_OF_PHAROS_SIRIUS }, { id: 601, item: GameData.Item.LOST_TREASURE_OF_TAM_TARA }, { id: 602, item: GameData.Item.LOST_TREASURE_OF_STONE_VIGIL },
		{ id: 634, item: GameData.Item.MAHATMA_OF_THE_RAM }, { id: 635, item: GameData.Item.MAHATMA_OF_THE_BULL }, { id: 636, item: GameData.Item.MAHATMA_OF_THE_TWINS }, { id: 637, item: GameData.Item.MAHATMA_OF_THE_CRAB },
		{ id: 638, item: GameData.Item.MAHATMA_OF_THE_LION }, { id: 639, item: GameData.Item.MAHATMA_OF_THE_MAIDEN }, { id: 640, item: GameData.Item.MAHATMA_OF_THE_SCALES }, { id: 641, item: GameData.Item.MAHATMA_OF_THE_SCORPION },
		{ id: 642, item: GameData.Item.MAHATMA_OF_THE_ARCHER }, { id: 643, item: GameData.Item.MAHATMA_OF_THE_GOAT }, { id: 644, item: GameData.Item.MAHATMA_OF_THE_WATERBEARER }, { id: 645, item: GameData.Item.MAHATMA_OF_THE_FISH },
		{ id: 654, item: GameData.Item.LUMINOUS_WIND_CRYSTAL }, { id: 661, item: GameData.Item.LUMINOUS_FIRE_CRYSTAL }, { id: 662, item: GameData.Item.LUMINOUS_LIGHTNING_CRYSTAL }, { id: 663, item: GameData.Item.LUMINOUS_ICE_CRYSTAL },
		{ id: 664, item: GameData.Item.LUMINOUS_EARTH_CRYSTAL }, { id: 665, item: GameData.Item.LUMINOUS_WATER_CRYSTAL }, { id: 694, item: GameData.Item.UNIDENTIFIABLE_BONE }, { id: 695, item: GameData.Item.UNIDENTIFIABLE_SHELL },
		{ id: 696, item: GameData.Item.UNIDENTIFIABLE_ORE }, { id: 697, item: GameData.Item.UNIDENTIFIABLE_SEEDS }, { id: 716, item: GameData.Item.ADAMANTITE_FRANCESCA }, { id: 717, item: GameData.Item.TITANIUM_ALLOY_MIRROR },
		{ id: 718, item: GameData.Item.DISPELLING_ARROW }, { id: 719, item: GameData.Item.KINGCAKE }, { id: 720, item: GameData.Item.SUPERIOR_ENCHANTED_INK }, { id: 736, item: GameData.Item.ODDLY_SPECIFIC_PETRIFIED_ORB_HQ },
		{ id: 737, item: GameData.Item.ODDLY_SPECIFIC_RIVETS_HQ }, { id: 738, item: GameData.Item.ODDLY_SPECIFIC_WIRE_HQ }, { id: 739, item: GameData.Item.ODDLY_SPECIFIC_WHETSTONE_HQ }, { id: 740, item: GameData.Item.ODDLY_SPECIFIC_LEATHER_HQ },
		{ id: 741, item: GameData.Item.ODDLY_SPECIFIC_MOONBEAM_SILK_HQ }, { id: 742, item: GameData.Item.ODDLY_SPECIFIC_SYNTHETIC_RESIN_HQ }, { id: 743, item: GameData.Item.ODDLY_SPECIFIC_SEED_EXTRACT_HQ },
		{ id: 744, item: GameData.Item.ODDLY_SPECIFIC_OBSIDIAN }, { id: 745, item: GameData.Item.ODDLY_SPECIFIC_MINERAL_SAND }, { id: 746, item: GameData.Item.ODDLY_SPECIFIC_LATEX }, { id: 747, item: GameData.Item.ODDLY_SPECIFIC_FOSSIL_DUST },
		{ id: 748, item: GameData.Item.THINKERS_CORAL }, { id: 749, item: GameData.Item.ODDLY_SPECIFIC_SHAFT_HQ }, { id: 750, item: GameData.Item.ODDLY_SPECIFIC_FITTING_HQ }, { id: 751, item: GameData.Item.ODDLY_SPECIFIC_CHAINMAIL_SHEET_HQ },
		{ id: 752, item: GameData.Item.ODDLY_SPECIFIC_GEMSTONE_HQ }, { id: 753, item: GameData.Item.ODDLY_SPECIFIC_VELLUM_HQ }, { id: 754, item: GameData.Item.ODDLY_SPECIFIC_VELVET_HQ }, { id: 755, item: GameData.Item.ODDLY_SPECIFIC_GLASS_HQ },
		{ id: 756, item: GameData.Item.ODDLY_SPECIFIC_SEED_FLOUR_HQ }, { id: 757, item: GameData.Item.ODDLY_SPECIFIC_DARK_MATTER }, { id: 758, item: GameData.Item.ODDLY_SPECIFIC_STRIKING_STONE }, { id: 759, item: GameData.Item.ODDLY_SPECIFIC_AMBER },
		{ id: 760, item: GameData.Item.ODDLY_SPECIFIC_BAUBLE }, { id: 761, item: GameData.Item.DRAGONSPINE }, { id: 762, item: GameData.Item.ODDLY_SPECIFIC_CEDAR_LOG }, { id: 763, item: GameData.Item.LIGNUM_VITAE_LOG },
		{ id: 764, item: GameData.Item.ODDLY_SPECIFIC_CEDAR_LUMBER }, { id: 765, item: GameData.Item.ODDLY_SPECIFIC_COERTHAN_IRON_ORE }, { id: 766, item: GameData.Item.DIMYTHRITE_ORE }, { id: 767, item: GameData.Item.ODDLY_SPECIFIC_IRON_NAILS },
		{ id: 768, item: GameData.Item.ODDLY_SPECIFIC_MYTHRITE_SAND }, { id: 769, item: GameData.Item.ODDLY_SPECIFIC_MYTHRIL_RINGS }, { id: 770, item: GameData.Item.ODDLY_SPECIFIC_SILVER_ORE }, { id: 771, item: GameData.Item.DIMYTHRITE_SAND },
		{ id: 772, item: GameData.Item.ODDLY_SPECIFIC_SILVER_NUGGET }, { id: 773, item: GameData.Item.ODDLY_SPECIFIC_GAGANA_SKIN }, { id: 774, item: GameData.Item.YELLOW_ALUMEN }, { id: 775, item: GameData.Item.ODDLY_SPECIFIC_GAGANASKIN_STRAP },
		{ id: 776, item: GameData.Item.ODDLY_SPECIFIC_FLEECE }, { id: 777, item: GameData.Item.DWARVEN_COTTON_BOLL }, { id: 778, item: GameData.Item.ODDLY_SPECIFIC_CLOTH }, { id: 779, item: GameData.Item.ODDLY_SPECIFIC_SAP },
		{ id: 780, item: GameData.Item.VAMPIRE_CUP_VINE }, { id: 781, item: GameData.Item.ODDLY_SPECIFIC_GLUE }, { id: 782, item: GameData.Item.ODDLY_SPECIFIC_ALOE }, { id: 783, item: GameData.Item.FRANTOIO }, { id: 784, item: GameData.Item.ODDLY_SPECIFIC_OIL },
		{ id: 897, item: GameData.Item.THAVNAIRIAN_SCALEPOWDER }, { id: 903, item: GameData.Item.TORTURED_MEMORY_OF_THE_DYING }, { id: 904, item: GameData.Item.SORROWFUL_MEMORY_OF_THE_DYING },
		{ id: 905, item: GameData.Item.HARROWING_MEMORY_OF_THE_DYING }, { id: 909, item: GameData.Item.BITTER_MEMORY_OF_THE_DYING }, { id: 913, item: GameData.Item.LOATHSOME_MEMORY_OF_THE_DYING },
		{ id: 918, item: GameData.Item.HAUNTING_MEMORY_OF_THE_DYING }, { id: 919, item: GameData.Item.VEXATIOUS_MEMORY_OF_THE_DYING }, { id: 922, item: GameData.Item.TIMEWORN_ARTIFACTS }, { id: 954, item: GameData.Item.ODDLY_SPECIFIC_SCHORL },
		{ id: 955, item: GameData.Item.ODDLY_SPECIFIC_LANDBORNE_AETHERSAND }, { id: 956, item: GameData.Item.ODDLY_SPECIFIC_DARK_CHESTNUT_LOG }, { id: 957, item: GameData.Item.ODDLY_SPECIFIC_LEAFBORNE_AETHERSAND },
		{ id: 958, item: GameData.Item.PETAL_SHELL }, { id: 961, item: GameData.Item.LIGNUM_VITAE_LUMBER }, { id: 962, item: GameData.Item.ODDLY_SPECIFIC_CEDAR_PLANK }, { id: 963, item: GameData.Item.DWARVEN_MYTHRIL_INGOT },
		{ id: 964, item: GameData.Item.ODDLY_SPECIFIC_IRON_INGOT }, { id: 965, item: GameData.Item.ODDLY_SPECIFIC_MYTHRIL_PLATE }, { id: 966, item: GameData.Item.DWARVEN_MYTHRIL_NUGGET }, { id: 967, item: GameData.Item.ODDLY_SPECIFIC_SILVER_INGOT },
		{ id: 968, item: GameData.Item.SEA_SWALLOW_LEATHER }, { id: 969, item: GameData.Item.ODDLY_SPECIFIC_GAGANA_LEATHER }, { id: 970, item: GameData.Item.DWARVEN_COTTON }, { id: 971, item: GameData.Item.ODDLY_SPECIFIC_UNDYED_WOOLEN_CLOTH },
		{ id: 972, item: GameData.Item.REFINED_NATRON }, { id: 973, item: GameData.Item.ODDLY_SPECIFIC_RUBBER }, { id: 974, item: GameData.Item.NIGHT_VINEGAR }, { id: 975, item: GameData.Item.ODDLY_SPECIFIC_PASTE },
		{ id: 994, item: GameData.Item.ODDLY_SPECIFIC_PRIMORDIAL_ORE }, { id: 995, item: GameData.Item.ODDLY_SPECIFIC_PRIMORDIAL_ASPHALTUM }, { id: 996, item: GameData.Item.ODDLY_SPECIFIC_PRIMORDIAL_LOG },
		{ id: 997, item: GameData.Item.ODDLY_SPECIFIC_PRIMORDIAL_RESIN }, { id: 998, item: GameData.Item.ALLAGAN_HUNTER }, { id: 999, item: GameData.Item.ODDLY_DELICATE_PINE_LOG }, { id: 1000, item: GameData.Item.GRADE_4_ARTISANAL_SKYBUILDERS_LOG },
		{ id: 1001, item: GameData.Item.GRADE_4_ARTISANAL_SKYBUILDERS_BARBGRASS }, { id: 1002, item: GameData.Item.ODDLY_DELICATE_SILVERGRACE_ORE }, { id: 1003, item: GameData.Item.GRADE_4_ARTISANAL_SKYBUILDERS_CLOUDSTONE },
		{ id: 1004, item: GameData.Item.GRADE_4_ARTISANAL_SKYBUILDERS_SILEX }, { id: 1005, item: GameData.Item.ODDLY_DELICATE_SCHEELITE }, { id: 1007, item: GameData.Item.GRADE_4_ARTISANAL_SKYBUILDERS_PRISMSTONE },
		{ id: 1008, item: GameData.Item.ODDLY_DELICATE_RAW_CELESTINE }, { id: 1011, item: GameData.Item.ODDLY_DELICATE_GAZELLE_HIDE }, { id: 1013, item: GameData.Item.GRADE_4_ARTISANAL_SKYBUILDERS_CAIMAN },
		{ id: 1014, item: GameData.Item.ODDLY_DELICATE_RHEA }, { id: 1015, item: GameData.Item.GRADE_4_ARTISANAL_SKYBUILDERS_COCOON }, { id: 1016, item: GameData.Item.GRADE_4_ARTISANAL_SKYBUILDERS_ICE_STALAGMITE },
		{ id: 1017, item: GameData.Item.ODDLY_DELICATE_MISTLETOE }, { id: 1018, item: GameData.Item.GRADE_4_ARTISANAL_SKYBUILDERS_SPRING_WATER }, { id: 1019, item: GameData.Item.GRADE_4_ARTISANAL_SKYBUILDERS_RASPBERRY },
		{ id: 1020, item: GameData.Item.ODDLY_DELICATE_HAMMERHEAD_SHARK }, { id: 1023, item: GameData.Item.ODDLY_DELICATE_ADAMANTITE_ORE }, { id: 1024, item: GameData.Item.ODDLY_DELICATE_RAW_JADE },
		{ id: 1025, item: GameData.Item.ODDLY_DELICATE_FEATHER }, { id: 1026, item: GameData.Item.ODDLY_DELICATE_BIRCH_LOG }, { id: 1027, item: GameData.Item.FLINTSTRIKE }, { id: 1028, item: GameData.Item.PICKLED_POM },
		{ id: 785, item: GameData.Item.ODDLY_DELICATE_PINE_LUMBER }, { id: 786, item: GameData.Item.ODDLY_DELICATE_SILVER_GEAR }, { id: 788, item: GameData.Item.ODDLY_DELICATE_WOLFRAM_SQUARE }, { id: 789, item: GameData.Item.ODDLY_DELICATE_CELESTINE },
		{ id: 791, item: GameData.Item.ODDLY_DELICATE_GAZELLE_LEATHER }, { id: 792, item: GameData.Item.ODDLY_DELICATE_RHEA_CLOTH }, { id: 794, item: GameData.Item.ODDLY_DELICATE_HOLY_WATER }, { id: 795, item: GameData.Item.ODDLY_DELICATE_SHARK_OIL },
		{ id: 861, item: GameData.Item.AETHER_OIL }, { id: 978, item: GameData.Item.CRYSTAL_SAND }, { id: 1029, item: GameData.Item.UMBRITE }, { id: 1037, item: GameData.Item.SINGING_CLUSTER }, { id: 1055, item: GameData.Item.PNEUMITE },
		{ id: 1058, item: GameData.Item.AETHERIC_DENSITY }, { id: 1083, item: GameData.Item.ARCHAIC_ENCHANTED_INK }, { id: 1012, item: GameData.Item.PROTEAN_CRYSTAL }, { id: 1022, item: GameData.Item.PAZUZUS_FEATHER },
		{ id: 1087, item: GameData.Item.PAGOS_CRYSTAL }, { id: 1088, item: GameData.Item.FROSTED_PROTEAN_CRYSTAL }, { id: 1089, item: GameData.Item.LOUHIS_ICE }, { id: 1090, item: GameData.Item.PYROS_CRYSTAL },
		{ id: 1092, item: GameData.Item.PENTHESILEAS_FLAME }, { id: 1101, item: GameData.Item.HYDATOS_CRYSTAL }, { id: 1102, item: GameData.Item.CRYSTALLINE_SCALE }, { id: 1105, item: GameData.Item.EUREKA_FRAGMENT }, { id: 1110, item: GameData.Item.ALEXANDRITE },
		// { id: 1111, name: "Materia I-IV" }, // Not used as an item anymore
		{ id: 1113, item: GameData.Item.COMPACT_AXLE }, { id: 1114, item: GameData.Item.COMPACT_SPRING }, { id: 1115, item: GameData.Item.BATTLES_FOR_THE_REALM }, { id: 1116, item: GameData.Item.BEYOND_THE_RIFT },
		{ id: 1117, item: GameData.Item.BLEAK_MEMORY_OF_THE_DYING }, { id: 1118, item: GameData.Item.LURID_MEMORY_OF_THE_DYING }, { id: 1119, item: GameData.Item.RAW_EMOTION },
	];
}

function getOldRelics():OldRelic[]{
	
	return [
		{ id: 247, relic: RelicData.Group.WEAPON_ZODIAC, parts: [
			{ id: 248, part: RelicData.Part.WEAPON_BASE_WEAPON, steps: [
				{ id: 249, step: RelicData.Step.BROKEN_WEAPON, tasks: [] },
				{ id: 250, step: RelicData.Step.CLASS_WEAPON, tasks: [
					{ id: 284, task: RelicData.Task.TASK_1, jobs: [Job.BRD] }, // brd -> longarm's composite bow
					{ id: 285, task: RelicData.Task.TASK_1, jobs: [Job.NIN] }, // nin -> vamper's knives
					{ id: 286, task: RelicData.Task.TASK_1, jobs: [Job.BLM] }, // blm -> sanguine scepter
					{ id: 287, task: RelicData.Task.TASK_1, jobs: [Job.SMN] }, // smn -> erudite's picatrix of casting
					{ id: 288, task: RelicData.Task.TASK_1, jobs: [Job.DRG] }, // drg -> champion's lance
					{ id: 289, task: RelicData.Task.TASK_1, jobs: [Job.MNK] }, // mnk -> wildling's cesti
					{ id: 290, task: RelicData.Task.TASK_1, jobs: [Job.PLD] }, // pld -> aeolian scimitar
					{ id: 291, task: RelicData.Task.TASK_1, jobs: [Job.WAR] }, // war -> barbarian's bardiche
					{ id: 292, task: RelicData.Task.TASK_1, jobs: [Job.SCH] }, // sch -> erudite's picatrix of healing
					{ id: 293, task: RelicData.Task.TASK_1, jobs: [Job.WHM] }, // whm -> madmans whispering rod
					{ id: 294, task: RelicData.Task.TASK_2, jobs: [Job.NIN, Job.BRD] }, // nin -> heavens eye materia, brd -> heavens eye
					{ id: 295, task: RelicData.Task.TASK_2, jobs: [Job.BLM, Job.SMN] }, // blm -> savage might materia, smn -> savage might
					{ id: 296, task: RelicData.Task.TASK_2, jobs: [Job.MNK, Job.DRG] }, // mnk -> savage aim materia, drg -> savage aim
					{ id: 297, task: RelicData.Task.TASK_2, jobs: [Job.PLD, Job.WAR] }, // pld -> battledance materia, war -> battledance
					{ id: 298, task: RelicData.Task.TASK_2, jobs: [Job.WHM, Job.SCH] }, // whm -> quicktongue materia, sch -> quicktongue
				] },
				{ id: 251, step: RelicData.Step.CHIMERA, tasks: [] },
				{ id: 252, step: RelicData.Step.AMDAPOR, tasks: [] },
				{ id: 253, step: RelicData.Step.BEASTMAN, tasks: [
					{ id: 299, task: RelicData.Task.TASK_1, jobs: [Job.BRD] }, // brd -> natalan boldwing
					{ id: 494, task: RelicData.Task.TASK_2, jobs: [Job.BRD] }, // brd -> natalan fogcaller
					{ id: 504, task: RelicData.Task.TASK_3, jobs: [Job.BRD] }, // brd -> natalan windtalon
					{ id: 300, task: RelicData.Task.TASK_1, jobs: [Job.BLM] }, // blm -> 13th order quarryman
					{ id: 495, task: RelicData.Task.TASK_2, jobs: [Job.BLM] }, // blm -> 13th order bedesman
					{ id: 505, task: RelicData.Task.TASK_3, jobs: [Job.BLM] }, // blm -> 13th order priest
					{ id: 301, task: RelicData.Task.TASK_1, jobs: [Job.DRG] }, // drg -> natalan boldwing
					{ id: 496, task: RelicData.Task.TASK_2, jobs: [Job.DRG] }, // drg -> natalan fogcaller
					{ id: 506, task: RelicData.Task.TASK_2, jobs: [Job.DRG] }, // drg -> natalan windtalon
					{ id: 302, task: RelicData.Task.TASK_1, jobs: [Job.MNK] }, // mnk -> zahar'ak lancer
					{ id: 497, task: RelicData.Task.TASK_2, jobs: [Job.MNK] }, // mnk -> zahar'ak pugilist
					{ id: 507, task: RelicData.Task.TASK_3, jobs: [Job.MNK] }, // mnk -> zahar'ak archer
					{ id: 303, task: RelicData.Task.TASK_1, jobs: [Job.PLD] }, // pld -> zahar'ak lancer
					{ id: 498, task: RelicData.Task.TASK_2, jobs: [Job.PLD] }, // pld -> zahar'ak pugilist
					{ id: 508, task: RelicData.Task.TASK_3, jobs: [Job.PLD] }, // pld -> zahar'ak thaumaturge
					{ id: 304, task: RelicData.Task.TASK_1, jobs: [Job.SCH] }, // sch -> sapsa shelfspine
					{ id: 499, task: RelicData.Task.TASK_2, jobs: [Job.SCH] }, // sch -> sapsa shelfclaw
					{ id: 509, task: RelicData.Task.TASK_3, jobs: [Job.SCH] }, // sch -> sapsa shelftooth
					{ id: 305, task: RelicData.Task.TASK_1, jobs: [Job.NIN] }, // nin -> sapsa shelfspine
					{ id: 500, task: RelicData.Task.TASK_2, jobs: [Job.NIN] }, // nin -> sapsa shelfclaw
					{ id: 510, task: RelicData.Task.TASK_3, jobs: [Job.NIN] }, // nin -> sapsa shelftooth
					{ id: 306, task: RelicData.Task.TASK_1, jobs: [Job.SMN] }, // smn -> violet sigh
					{ id: 501, task: RelicData.Task.TASK_2, jobs: [Job.SMN] }, // smn -> violet screech
					{ id: 511, task: RelicData.Task.TASK_3, jobs: [Job.SMN] }, // smn -> violet snarl
					{ id: 307, task: RelicData.Task.TASK_1, jobs: [Job.WAR] }, // war -> 13th order quarryman
					{ id: 502, task: RelicData.Task.TASK_2, jobs: [Job.WAR] }, // war -> 13th order bedesman
					{ id: 512, task: RelicData.Task.TASK_3, jobs: [Job.WAR] }, // war -> 13th order priest
					{ id: 308, task: RelicData.Task.TASK_1, jobs: [Job.WHM] }, // whm -> 13th order quarryman
					{ id: 503, task: RelicData.Task.TASK_2, jobs: [Job.WHM] }, // whm -> 13th order bedesman
					{ id: 513, task: RelicData.Task.TASK_3, jobs: [Job.WHM] }, // whm -> 13th order priest
				] },
				{ id: 254, step: RelicData.Step.HYDRA, tasks: [] },
				{ id: 255, step: RelicData.Step.EMBER, tasks: [] },
				{ id: 256, step: RelicData.Step.GALE, tasks: [] },
				{ id: 257, step: RelicData.Step.ORE, tasks: [] },
				{ id: 258, step: RelicData.Step.OIL, tasks: [{ id: 309, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] }] },
			] },
			{ id: 259, part: RelicData.Part.WEAPON_ZENITH, steps: [
				{ id: 283, step: RelicData.Step.MIST, tasks: [
					{ id: 310, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
				] },
			] },
			{ id: 260, part: RelicData.Part.WEAPON_ATMA, steps: [
				{ id: 1163, step: RelicData.Step.ARMS, tasks: [] },
				{ id: 261, step: RelicData.Step.ATMAS, tasks: [
					{ id: 311, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 312, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 313, task: RelicData.Task.TASK_3, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 314, task: RelicData.Task.TASK_4, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 315, task: RelicData.Task.TASK_5, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 316, task: RelicData.Task.TASK_6, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 317, task: RelicData.Task.TASK_7, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 318, task: RelicData.Task.TASK_8, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 319, task: RelicData.Task.TASK_9, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 320, task: RelicData.Task.TASK_10, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 321, task: RelicData.Task.TASK_11, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 322, task: RelicData.Task.TASK_12, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
				] },
			] },
			{ id: 262, part: RelicData.Part.WEAPON_ANIMUS, steps: [
				{ id: 1164, step: RelicData.Step.TRIALS, tasks: [] },
				{ id: 263, step: RelicData.Step.SKYFIRE_1, tasks: [
					{ id: 323, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 324, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 325, task: RelicData.Task.TASK_3, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 326, task: RelicData.Task.TASK_4, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 327, task: RelicData.Task.TASK_5, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 328, task: RelicData.Task.TASK_6, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 329, task: RelicData.Task.TASK_7, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 330, task: RelicData.Task.TASK_8, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 331, task: RelicData.Task.TASK_9, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 332, task: RelicData.Task.TASK_10, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 333, task: RelicData.Task.TASK_11, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 334, task: RelicData.Task.TASK_12, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 335, task: RelicData.Task.TASK_13, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 336, task: RelicData.Task.TASK_14, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 337, task: RelicData.Task.TASK_15, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 338, task: RelicData.Task.TASK_16, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 339, task: RelicData.Task.TASK_17, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 340, task: RelicData.Task.TASK_18, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 341, task: RelicData.Task.TASK_19, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
				] },
				{ id: 264, step: RelicData.Step.SKYFIRE_2, tasks: [
					{ id: 342, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 343, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 344, task: RelicData.Task.TASK_3, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 345, task: RelicData.Task.TASK_4, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 346, task: RelicData.Task.TASK_5, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 347, task: RelicData.Task.TASK_6, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 348, task: RelicData.Task.TASK_7, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 349, task: RelicData.Task.TASK_8, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 350, task: RelicData.Task.TASK_9, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 351, task: RelicData.Task.TASK_10, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 352, task: RelicData.Task.TASK_11, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 353, task: RelicData.Task.TASK_12, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 354, task: RelicData.Task.TASK_13, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 355, task: RelicData.Task.TASK_14, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 356, task: RelicData.Task.TASK_15, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 357, task: RelicData.Task.TASK_16, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 358, task: RelicData.Task.TASK_17, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 359, task: RelicData.Task.TASK_18, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 360, task: RelicData.Task.TASK_19, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
				] },
				{ id: 265, step: RelicData.Step.NETHERFIRE_1, tasks: [
					{ id: 361, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 362, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 363, task: RelicData.Task.TASK_3, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 364, task: RelicData.Task.TASK_4, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 365, task: RelicData.Task.TASK_5, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 366, task: RelicData.Task.TASK_6, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 367, task: RelicData.Task.TASK_7, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 368, task: RelicData.Task.TASK_8, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 369, task: RelicData.Task.TASK_9, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 370, task: RelicData.Task.TASK_10, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 371, task: RelicData.Task.TASK_11, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 372, task: RelicData.Task.TASK_12, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 373, task: RelicData.Task.TASK_13, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 374, task: RelicData.Task.TASK_14, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 375, task: RelicData.Task.TASK_15, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 376, task: RelicData.Task.TASK_16, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 377, task: RelicData.Task.TASK_17, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 378, task: RelicData.Task.TASK_18, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 379, task: RelicData.Task.TASK_19, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
				] },
				{ id: 266, step: RelicData.Step.SKYFALL_1, tasks: [
					{ id: 380, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 381, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 382, task: RelicData.Task.TASK_3, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 383, task: RelicData.Task.TASK_4, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 384, task: RelicData.Task.TASK_5, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 385, task: RelicData.Task.TASK_6, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 386, task: RelicData.Task.TASK_7, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 387, task: RelicData.Task.TASK_8, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 388, task: RelicData.Task.TASK_9, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 389, task: RelicData.Task.TASK_10, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 390, task: RelicData.Task.TASK_11, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 391, task: RelicData.Task.TASK_12, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 392, task: RelicData.Task.TASK_13, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 393, task: RelicData.Task.TASK_14, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 394, task: RelicData.Task.TASK_15, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 395, task: RelicData.Task.TASK_16, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 396, task: RelicData.Task.TASK_17, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 397, task: RelicData.Task.TASK_18, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 398, task: RelicData.Task.TASK_19, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
				] },
				{ id: 267, step: RelicData.Step.SKYFALL_2, tasks: [
					{ id: 399, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 400, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 401, task: RelicData.Task.TASK_3, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 402, task: RelicData.Task.TASK_4, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 403, task: RelicData.Task.TASK_5, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 404, task: RelicData.Task.TASK_6, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 405, task: RelicData.Task.TASK_7, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 406, task: RelicData.Task.TASK_8, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 407, task: RelicData.Task.TASK_9, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 408, task: RelicData.Task.TASK_10, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 409, task: RelicData.Task.TASK_11, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 410, task: RelicData.Task.TASK_12, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 411, task: RelicData.Task.TASK_13, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 412, task: RelicData.Task.TASK_14, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 413, task: RelicData.Task.TASK_15, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 414, task: RelicData.Task.TASK_16, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 415, task: RelicData.Task.TASK_17, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 416, task: RelicData.Task.TASK_18, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 417, task: RelicData.Task.TASK_19, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
				] },
				{ id: 268, step: RelicData.Step.NETHERFALL_1, tasks: [
					{ id: 418, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 419, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 420, task: RelicData.Task.TASK_3, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 421, task: RelicData.Task.TASK_4, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 422, task: RelicData.Task.TASK_5, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 423, task: RelicData.Task.TASK_6, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 424, task: RelicData.Task.TASK_7, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 425, task: RelicData.Task.TASK_8, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 426, task: RelicData.Task.TASK_9, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 427, task: RelicData.Task.TASK_10, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 428, task: RelicData.Task.TASK_11, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 429, task: RelicData.Task.TASK_12, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 430, task: RelicData.Task.TASK_13, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 431, task: RelicData.Task.TASK_14, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 432, task: RelicData.Task.TASK_15, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 433, task: RelicData.Task.TASK_16, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 434, task: RelicData.Task.TASK_17, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 435, task: RelicData.Task.TASK_18, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 436, task: RelicData.Task.TASK_19, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
				] },
				{ id: 269, step: RelicData.Step.SKYWIND_1, tasks: [
					{ id: 437, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 438, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 439, task: RelicData.Task.TASK_3, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 440, task: RelicData.Task.TASK_4, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 441, task: RelicData.Task.TASK_5, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 442, task: RelicData.Task.TASK_6, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 443, task: RelicData.Task.TASK_7, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 444, task: RelicData.Task.TASK_8, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 445, task: RelicData.Task.TASK_9, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 446, task: RelicData.Task.TASK_10, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 447, task: RelicData.Task.TASK_11, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 448, task: RelicData.Task.TASK_12, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 449, task: RelicData.Task.TASK_13, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 450, task: RelicData.Task.TASK_14, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 451, task: RelicData.Task.TASK_15, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 452, task: RelicData.Task.TASK_16, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 453, task: RelicData.Task.TASK_17, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 454, task: RelicData.Task.TASK_18, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 455, task: RelicData.Task.TASK_19, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
				] },
				{ id: 270, step: RelicData.Step.SKYWIND_2, tasks: [
					{ id: 456, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 457, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 458, task: RelicData.Task.TASK_3, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 459, task: RelicData.Task.TASK_4, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 460, task: RelicData.Task.TASK_5, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 461, task: RelicData.Task.TASK_6, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 462, task: RelicData.Task.TASK_7, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 463, task: RelicData.Task.TASK_8, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 464, task: RelicData.Task.TASK_9, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 465, task: RelicData.Task.TASK_10, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 466, task: RelicData.Task.TASK_11, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 467, task: RelicData.Task.TASK_12, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 468, task: RelicData.Task.TASK_13, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 469, task: RelicData.Task.TASK_14, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 470, task: RelicData.Task.TASK_15, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 471, task: RelicData.Task.TASK_16, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 472, task: RelicData.Task.TASK_17, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 473, task: RelicData.Task.TASK_18, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 474, task: RelicData.Task.TASK_19, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
				] },
				{ id: 271, step: RelicData.Step.SKYEARTH_1, tasks: [
					{ id: 475, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 476, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 477, task: RelicData.Task.TASK_3, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 478, task: RelicData.Task.TASK_4, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 479, task: RelicData.Task.TASK_5, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 480, task: RelicData.Task.TASK_6, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 481, task: RelicData.Task.TASK_7, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 482, task: RelicData.Task.TASK_8, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 483, task: RelicData.Task.TASK_9, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 484, task: RelicData.Task.TASK_10, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 485, task: RelicData.Task.TASK_11, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 486, task: RelicData.Task.TASK_12, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 487, task: RelicData.Task.TASK_13, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 488, task: RelicData.Task.TASK_14, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 489, task: RelicData.Task.TASK_15, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 490, task: RelicData.Task.TASK_16, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 491, task: RelicData.Task.TASK_17, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 492, task: RelicData.Task.TASK_18, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 493, task: RelicData.Task.TASK_19, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
				] },
			] },
			{ id: 272, part: RelicData.Part.WEAPON_NOVUS, steps: [
				{ id: 1096, step: RelicData.Step.RADIANCE, tasks: [] },
				{ id: 273, step: RelicData.Step.SCROLL, tasks: [
					{ id: 721, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
				] },
				{ id: 1165, step: RelicData.Step.STAR, tasks: [] },
				{ id: 1108, step: RelicData.Step.ALEXANDRITE, tasks: [
					{ id: 1109, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
				] },
				{ id: 274, step: RelicData.Step.BINDING, tasks: [] },
			] },
			{ id: 529, part: RelicData.Part.WEAPON_NEXUS, steps: [
				{ id: 1167, step: RelicData.Step.SOULGLAZE, tasks: [] },
				{ id: 530, step: RelicData.Step.LIGHT, tasks: [
					{ id: 543, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
				] },
			] },
			{ id: 276, part: RelicData.Part.WEAPON_BRAVES, steps: [
				{ id: 1168, step: RelicData.Step.WHEREFORE, tasks: [] },
				{ id: 544, step: RelicData.Step.SKYLIGHT, tasks: [
					{ id: 549, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 550, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 551, task: RelicData.Task.TASK_3, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 552, task: RelicData.Task.TASK_4, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 553, task: RelicData.Task.TASK_5, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 554, task: RelicData.Task.TASK_6, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 555, task: RelicData.Task.TASK_7, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 556, task: RelicData.Task.TASK_8, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 557, task: RelicData.Task.TASK_9, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
				] },
				{ id: 545, step: RelicData.Step.ZODIUM, tasks: [
					{ id: 567, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 568, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 569, task: RelicData.Task.TASK_3, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 570, task: RelicData.Task.TASK_4, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 571, task: RelicData.Task.TASK_5, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 572, task: RelicData.Task.TASK_6, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 573, task: RelicData.Task.TASK_7, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 574, task: RelicData.Task.TASK_8, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 575, task: RelicData.Task.TASK_9, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
				] },
				{ id: 546, step: RelicData.Step.FLAWLESS_ALEXANDRITE, tasks: [
					{ id: 603, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 604, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 605, task: RelicData.Task.TASK_3, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 606, task: RelicData.Task.TASK_4, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 607, task: RelicData.Task.TASK_5, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 608, task: RelicData.Task.TASK_6, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 609, task: RelicData.Task.TASK_7, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 610, task: RelicData.Task.TASK_8, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 611, task: RelicData.Task.TASK_9, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
				] },
				{ id: 547, step: RelicData.Step.BRAVES_SCROLL, tasks: [
					{ id: 612, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 613, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 614, task: RelicData.Task.TASK_3, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 615, task: RelicData.Task.TASK_4, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 616, task: RelicData.Task.TASK_5, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 617, task: RelicData.Task.TASK_6, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 618, task: RelicData.Task.TASK_7, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 619, task: RelicData.Task.TASK_8, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 620, task: RelicData.Task.TASK_9, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
				] },
				{ id: 548, step: RelicData.Step.MATERIA, tasks: [] },
			] },
			{ id: 277, part: RelicData.Part.WEAPON_ZETA, steps: [
				{ id: 1171, step: RelicData.Step.SHINE, tasks: [] },
				{ id: 621, step: RelicData.Step.SOUL, tasks: [
					{ id: 622, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 623, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 624, task: RelicData.Task.TASK_3, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 625, task: RelicData.Task.TASK_4, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 626, task: RelicData.Task.TASK_5, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 627, task: RelicData.Task.TASK_6, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 628, task: RelicData.Task.TASK_7, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 629, task: RelicData.Task.TASK_8, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 630, task: RelicData.Task.TASK_9, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 631, task: RelicData.Task.TASK_10, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 632, task: RelicData.Task.TASK_11, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
					{ id: 633, task: RelicData.Task.TASK_12, jobs: jobLists.relic[RelicData.Group.WEAPON_ZODIAC] },
				] },
				{ id: 1172, step: RelicData.Step.VITAL, tasks: [] },
			] },
		] },
		{ id: 278, relic: RelicData.Group.WEAPON_ANIMA, parts: [
			{ id: 652, part: RelicData.Part.WEAPON_ANIMATED, steps: [
				{ id: 653, step: RelicData.Step.CRYSTALS, tasks: [
					{ id: 666, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 667, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 668, task: RelicData.Task.TASK_3, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 669, task: RelicData.Task.TASK_4, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 670, task: RelicData.Task.TASK_5, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 671, task: RelicData.Task.TASK_6, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
				] },
			] },
			{ id: 672, part: RelicData.Part.WEAPON_AWOKEN, steps: [
				{ id: 673, step: RelicData.Step.SNOWCLOAK, tasks: [] },
				{ id: 691, step: RelicData.Step.SASTASHA, tasks: [] },
				{ id: 275, step: RelicData.Step.QARN, tasks: [] },
				{ id: 722, step: RelicData.Step.KEEPER, tasks: [] },
				{ id: 723, step: RelicData.Step.WANDERER, tasks: [] },
				{ id: 724, step: RelicData.Step.AMDAPOR_AWOKEN, tasks: [] },
				{ id: 725, step: RelicData.Step.VIGIL, tasks: [] },
				{ id: 726, step: RelicData.Step.SOHM, tasks: [] },
				{ id: 727, step: RelicData.Step.AERY, tasks: [] },
				{ id: 728, step: RelicData.Step.VAULT, tasks: [] },
			] },
			{ id: 692, part: RelicData.Part.WEAPON_ANIMA, steps: [
				{ id: 814, step: RelicData.Step.RUBBER, tasks: [
					{ id: 701, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 843, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
				] },
				{ id: 698, step: RelicData.Step.CARBONCOAT, tasks: [
					{ id: 844, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 846, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
				] },
				{ id: 699, step: RelicData.Step.WATER, tasks: [
					{ id: 847, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 849, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
				] },
				{ id: 700, step: RelicData.Step.CATALYST, tasks: [
					{ id: 851, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 852, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
				] },
			] },
			{ id: 854, part: RelicData.Part.WEAPON_HYPERCONDUCTIVE, steps: [
				{ id: 857, step: RelicData.Step.OIL_HYPER, tasks: [
					{ id: 860, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
				] },
			] },
			{ id: 863, part: RelicData.Part.WEAPON_RECONDITIONED, steps: [
				{ id: 977, step: RelicData.Step.SAND, tasks: [
					{ id: 1030, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 1031, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
				] },
			] },
			{ id: 1034, part: RelicData.Part.WEAPON_SHARPENED, steps: [
				{ id: 1035, step: RelicData.Step.CLUSTERS, tasks: [
					{ id: 1036, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
				] },
			] },
			{ id: 1045, part: RelicData.Part.WEAPON_COMPLETE, steps: [
				{ id: 1046, step: RelicData.Step.DUNGEONS, tasks: [
					{ id: 1047, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 1048, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 1049, task: RelicData.Task.TASK_3, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
				] },
				{ id: 1053, step: RelicData.Step.PNEUMITE, tasks: [
					{ id: 1054, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
				] },
				{ id: 1056, step: RelicData.Step.DENSITY, tasks: [
					{ id: 1057, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
				] },
			] },
			{ id: 1059, part: RelicData.Part.WEAPON_LUX, steps: [
				{ id: 1060, step: RelicData.Step.BFF, tasks: [
					{ id: 1070, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 1071, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 1072, task: RelicData.Task.TASK_3, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 1073, task: RelicData.Task.TASK_4, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 1074, task: RelicData.Task.TASK_5, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 1075, task: RelicData.Task.TASK_6, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 1076, task: RelicData.Task.TASK_7, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 1077, task: RelicData.Task.TASK_8, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 1078, task: RelicData.Task.TASK_9, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 1079, task: RelicData.Task.TASK_10, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 1080, task: RelicData.Task.TASK_11, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 1081, task: RelicData.Task.TASK_12, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
					{ id: 1082, task: RelicData.Task.TASK_13, jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA] },
				] },
			] },
		] },
		{ id: 279, relic: RelicData.Group.WEAPON_EUREKA, parts: [
			{ id: 980, part: RelicData.Part.WEAPON_ANTIQUATED, steps: [
				{ id: 1006, step: RelicData.Step.ANTIQUATED, tasks: [] },
			] },
			{ id: 981, part: RelicData.Part.WEAPON_ANEMOS, steps: [
				{ id: 1188, step: RelicData.Step.ANEMOS_0, tasks: [
					{ id: 1191, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_EUREKA] },
				] },
				{ id: 1189, step: RelicData.Step.ANEMOS_1, tasks: [
					{ id: 1192, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_EUREKA] },
				] },
				{ id: 1190, step: RelicData.Step.ANEMOS_2, tasks: [
					{ id: 1193, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_EUREKA] },
				] },
				{ id: 1009, step: RelicData.Step.ANEMOS, tasks: [
					{ id: 1021, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_EUREKA] },
				] },
			] },
			{ id: 983, part: RelicData.Part.WEAPON_ELEMENTAL, steps: [
				{ id: 1010, step: RelicData.Step.PAGOS, tasks: [
					{ id: 1085, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_EUREKA] },
				] },
				{ id: 1194, step: RelicData.Step.PAGOS_1, tasks: [
					{ id: 1084, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_EUREKA] },
					{ id: 1195, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_EUREKA] },
				] },
				{ id: 984, step: RelicData.Step.ELEMENTAL, tasks: [
					{ id: 1196, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_EUREKA] },
					{ id: 1086, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_EUREKA] },
				] },
			] },
			{ id: 1093, part: RelicData.Part.WEAPON_PYROS, steps: [
				{ id: 1094, step: RelicData.Step.ELEMENTAL_1, tasks: [
					{ id: 1199, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_EUREKA] },
				] },
				{ id: 1197, step: RelicData.Step.ELEMENTAL_2, tasks: [
					{ id: 1200, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_EUREKA] },
				] },
				{ id: 1198, step: RelicData.Step.PYROS, tasks: [
					{ id: 1095, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_EUREKA] },
					{ id: 1097, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_EUREKA] },
				] },
			] },
			{ id: 986, part: RelicData.Part.WEAPON_EUREKA, steps: [
				{ id: 1098, step: RelicData.Step.HYDATOS, tasks: [
					{ id: 1099, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_EUREKA] },
				] },
				{ id: 1201, step: RelicData.Step.HYDATOS_1, tasks: [
					{ id: 1203, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_EUREKA] },
				] },
				{ id: 1205, step: RelicData.Step.BASE, tasks: [
					{ id: 1206, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_EUREKA] },
				] },
				{ id: 1202, step: RelicData.Step.EUREKA, tasks: [
					{ id: 1204, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_EUREKA] },
					{ id: 1100, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_EUREKA] },
				] },
			] },
			{ id: 987, part: RelicData.Part.WEAPON_PHYSEOS, steps: [
				{ id: 1103, step: RelicData.Step.FRAGMENTS, tasks: [
					{ id: 1104, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_EUREKA] },
				] },
			] },
		] },
		{ id: 280, relic: RelicData.Group.WEAPON_RESISTANCE, parts: [
			{ id: 894, part: RelicData.Part.WEAPON_RESISTANCE, steps: [
				{ id: 895, step: RelicData.Step.SCALEPOWDER, tasks: [
					{ id: 896, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_RESISTANCE] },
				] },
			] },
			{ id: 898, part: RelicData.Part.WEAPON_AUGMENTED_RESISTANCE, steps: [
				{ id: 899, step: RelicData.Step.MEMORY, tasks: [
					{ id: 900, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_RESISTANCE] },
					{ id: 901, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_RESISTANCE] },
					{ id: 902, task: RelicData.Task.TASK_3, jobs: jobLists.relic[RelicData.Group.WEAPON_RESISTANCE] },
				] },
			] },
			{ id: 906, part: RelicData.Part.WEAPON_RECOLLECTION, steps: [
				{ id: 907, step: RelicData.Step.RESIST, tasks: [
					{ id: 908, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_RESISTANCE] },
				] },
			] },
			{ id: 910, part: RelicData.Part.WEAPON_LAWS_ORDER, steps: [
				{ id: 911, step: RelicData.Step.CHANGE_OF_ARMS, tasks: [
					{ id: 912, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_RESISTANCE] },
				] },
			] },
			{ id: 914, part: RelicData.Part.WEAPON_AUGMENTED_LAWS_ORDER, steps: [
				{ id: 915, step: RelicData.Step.REMEMBERS, tasks: [
					{ id: 916, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_RESISTANCE] },
					{ id: 917, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_RESISTANCE] },
				] },
				{ id: 920, step: RelicData.Step.PATH, tasks: [
					{ id: 921, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_RESISTANCE] },
				] },
			] },
			{ id: 1106, part: RelicData.Part.WEAPON_BLADE, steps: [
				{ id: 1107, step: RelicData.Step.SPARE_PARTS, tasks: [
					{ id: 1120, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_RESISTANCE] },
					{ id: 1121, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_RESISTANCE] },
				] },
				{ id: 689, step: RelicData.Step.STORY, tasks: [
					{ id: 1122, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_RESISTANCE] },
					{ id: 1123, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_RESISTANCE] },
				] },
				{ id: 690, step: RelicData.Step.FOND_MEMORY, tasks: [
					{ id: 1124, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_RESISTANCE] },
					{ id: 1125, task: RelicData.Task.TASK_2, jobs: jobLists.relic[RelicData.Group.WEAPON_RESISTANCE] },
				] },
				{ id: 1091, step: RelicData.Step.DEAL, tasks: [] },
				{ id: 1112, step: RelicData.Step.IRRESISTIBLE, tasks: [
					{ id: 1126, task: RelicData.Task.TASK_1, jobs: jobLists.relic[RelicData.Group.WEAPON_RESISTANCE] },
				] },
			] },
		] },
		{ id: 281, relic: RelicData.Group.TOOL_SKYSTEEL, parts: [
			{ id: 729, part: RelicData.Part.TOOL_SKYSTEEL, steps: [
				{ id: 730, step: RelicData.Step.PURCHASE, tasks: [] },
			] },
			{ id: 809, part: RelicData.Part.TOOL_SKYSTEEL_1, steps: [
				{ id: 731, step: RelicData.Step.SKYSTEEL_ITEMS, tasks: [
					{ id: 817, task: RelicData.Task.TASK_1, jobs: [Job.CRP] },
					{ id: 818, task: RelicData.Task.TASK_1, jobs: [Job.BSM] },
					{ id: 819, task: RelicData.Task.TASK_1, jobs: [Job.ARM] },
					{ id: 820, task: RelicData.Task.TASK_1, jobs: [Job.GSM] },
					{ id: 821, task: RelicData.Task.TASK_1, jobs: [Job.LTW] },
					{ id: 822, task: RelicData.Task.TASK_1, jobs: [Job.WVR] },
					{ id: 823, task: RelicData.Task.TASK_1, jobs: [Job.ALC] },
					{ id: 824, task: RelicData.Task.TASK_1, jobs: [Job.CUL] },
					{ id: 825, task: RelicData.Task.TASK_1, jobs: [Job.MIN] },
					{ id: 826, task: RelicData.Task.TASK_2, jobs: [Job.MIN] },
					{ id: 827, task: RelicData.Task.TASK_1, jobs: [Job.BTN] },
					{ id: 828, task: RelicData.Task.TASK_2, jobs: [Job.BTN] },
					{ id: 829, task: RelicData.Task.TASK_1, jobs: [Job.FSH] },
				] },
			] },
			{ id: 810, part: RelicData.Part.TOOL_DRAGONSUNG, steps: [
				{ id: 732, step: RelicData.Step.DRAGONSUNG_ITEMS, tasks: [
					{ id: 830, task: RelicData.Task.TASK_1, jobs: [Job.CRP] },
					{ id: 831, task: RelicData.Task.TASK_1, jobs: [Job.BSM] },
					{ id: 832, task: RelicData.Task.TASK_1, jobs: [Job.ARM] },
					{ id: 833, task: RelicData.Task.TASK_1, jobs: [Job.GSM] },
					{ id: 834, task: RelicData.Task.TASK_1, jobs: [Job.LTW] },
					{ id: 835, task: RelicData.Task.TASK_1, jobs: [Job.WVR] },
					{ id: 836, task: RelicData.Task.TASK_1, jobs: [Job.ALC] },
					{ id: 837, task: RelicData.Task.TASK_1, jobs: [Job.CUL] },
					{ id: 838, task: RelicData.Task.TASK_1, jobs: [Job.MIN] },
					{ id: 839, task: RelicData.Task.TASK_2, jobs: [Job.MIN] },
					{ id: 840, task: RelicData.Task.TASK_1, jobs: [Job.BTN] },
					{ id: 841, task: RelicData.Task.TASK_2, jobs: [Job.BTN] },
					{ id: 842, task: RelicData.Task.TASK_1, jobs: [Job.FSH] },
				] },
			] },
			{ id: 811, part: RelicData.Part.TOOL_AUGMENTED_DRAGONSUNG, steps: [
				{ id: 733, step: RelicData.Step.BASIC_GOBBIEGOO, tasks: [
					{ id: 845, task: RelicData.Task.TASK_1, jobs: [Job.CRP] },
					{ id: 848, task: RelicData.Task.TASK_1, jobs: [Job.BSM] },
					{ id: 850, task: RelicData.Task.TASK_1, jobs: [Job.ARM] },
					{ id: 853, task: RelicData.Task.TASK_1, jobs: [Job.GSM] },
					{ id: 787, task: RelicData.Task.TASK_1, jobs: [Job.LTW] },
					{ id: 790, task: RelicData.Task.TASK_1, jobs: [Job.WVR] },
					{ id: 793, task: RelicData.Task.TASK_1, jobs: [Job.ALC] },
					{ id: 796, task: RelicData.Task.TASK_1, jobs: [Job.CUL] },
					{ id: 797, task: RelicData.Task.TASK_1, jobs: [Job.MIN] },
					{ id: 952, task: RelicData.Task.TASK_2, jobs: [Job.MIN] },
					{ id: 953, task: RelicData.Task.TASK_1, jobs: [Job.BTN] },
					{ id: 959, task: RelicData.Task.TASK_2, jobs: [Job.BTN] },
					{ id: 960, task: RelicData.Task.TASK_1, jobs: [Job.FSH] },
				] },
			] },
			{ id: 812, part: RelicData.Part.TOOL_SKYSUNG, steps: [
				{ id: 734, step: RelicData.Step.VISCOUS_GOBBIEGOO, tasks: [
					{ id: 856, task: RelicData.Task.TASK_1, jobs: [Job.CRP] },
					{ id: 859, task: RelicData.Task.TASK_1, jobs: [Job.BSM] },
					{ id: 862, task: RelicData.Task.TASK_1, jobs: [Job.ARM] },
					{ id: 976, task: RelicData.Task.TASK_1, jobs: [Job.GSM] },
					{ id: 979, task: RelicData.Task.TASK_1, jobs: [Job.LTW] },
					{ id: 982, task: RelicData.Task.TASK_1, jobs: [Job.WVR] },
					{ id: 985, task: RelicData.Task.TASK_1, jobs: [Job.ALC] },
					{ id: 988, task: RelicData.Task.TASK_1, jobs: [Job.CUL] },
					{ id: 989, task: RelicData.Task.TASK_1, jobs: [Job.MIN] },
					{ id: 990, task: RelicData.Task.TASK_2, jobs: [Job.MIN] },
					{ id: 991, task: RelicData.Task.TASK_1, jobs: [Job.BTN] },
					{ id: 992, task: RelicData.Task.TASK_2, jobs: [Job.BTN] },
					{ id: 993, task: RelicData.Task.TASK_1, jobs: [Job.FSH] },
				] },
			] },
			{ id: 813, part: RelicData.Part.TOOL_SKYBUILDERS, steps: [
				{ id: 735, step: RelicData.Step.PARTS, tasks: [
					{ id: 865, task: RelicData.Task.TASK_1, jobs: [Job.CRP] },
					{ id: 868, task: RelicData.Task.TASK_1, jobs: [Job.BSM] },
					{ id: 871, task: RelicData.Task.TASK_1, jobs: [Job.ARM] },
					{ id: 876, task: RelicData.Task.TASK_1, jobs: [Job.GSM] },
					{ id: 877, task: RelicData.Task.TASK_1, jobs: [Job.LTW] },
					{ id: 1032, task: RelicData.Task.TASK_1, jobs: [Job.WVR] },
					{ id: 1033, task: RelicData.Task.TASK_1, jobs: [Job.ALC] },
					{ id: 1038, task: RelicData.Task.TASK_1, jobs: [Job.CUL] },
					{ id: 1039, task: RelicData.Task.TASK_1, jobs: [Job.MIN] },
					{ id: 1040, task: RelicData.Task.TASK_2, jobs: [Job.MIN] },
					{ id: 1041, task: RelicData.Task.TASK_1, jobs: [Job.BTN] },
					{ id: 1042, task: RelicData.Task.TASK_2, jobs: [Job.BTN] },
					{ id: 1043, task: RelicData.Task.TASK_1, jobs: [Job.FSH] },
					{ id: 1044, task: RelicData.Task.TASK_2, jobs: [Job.FSH] },
				] },
			] },
		] },
	];
}
